import { jsx as _jsx } from "react/jsx-runtime";
/**
 * CopyButton — small icon button that copies a string to the clipboard and
 * surfaces a toast confirmation.
 *
 * Used on the ticket detail page so agents can grab the ticket number,
 * requester email, or POC contact details into an outbound vendor email
 * with one click.
 *
 * Props:
 *   value           — the string to copy
 *   label           — what to call this thing in the success toast
 *                     (e.g. "Ticket number", "POC email")
 *   className       — optional extra classes
 *   showInlineCheck — when true, shows a 1-second check icon next to
 *                     the button after a successful copy. Defaults to true.
 *
 * Failure mode:
 *   navigator.clipboard isn't available on insecure origins (http:// over
 *   the public internet) and some embedded WebViews. We fall back to a
 *   transient <textarea> + execCommand('copy'); if even that fails we
 *   fire an error toast and leave the value selected so the agent can
 *   Ctrl-C it manually.
 */
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/components/ui/Toast';
export function CopyButton({ value, label, className, showInlineCheck = true, }) {
    const toast = useToast();
    const [copied, setCopied] = useState(false);
    const onClick = async (e) => {
        // Stop click bubbling — many of our list/card rows are themselves
        // clickable, and copying a value should not also navigate away.
        e.stopPropagation();
        e.preventDefault();
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(value);
            }
            else {
                // Legacy fallback for http:// dev previews.
                const ta = document.createElement('textarea');
                ta.value = value;
                ta.setAttribute('readonly', '');
                ta.style.position = 'absolute';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.select();
                const ok = document.execCommand('copy');
                document.body.removeChild(ta);
                if (!ok)
                    throw new Error('execCommand copy returned false');
            }
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
            toast.success(`${label} copied`, value.length > 60 ? `${value.slice(0, 57)}…` : value);
        }
        catch (err) {
            toast.error(`Couldn't copy ${label.toLowerCase()}`, err instanceof Error ? err.message : 'Clipboard access denied by the browser.');
        }
    };
    return (_jsx("button", { type: "button", onClick: onClick, title: `Copy ${label.toLowerCase()} to clipboard`, "aria-label": `Copy ${label.toLowerCase()} to clipboard`, className: [
            'inline-flex items-center justify-center rounded-md p-1 text-slate-500 transition-colors',
            'hover:bg-slate-100 hover:text-slate-700',
            'dark:hover:bg-slate-800 dark:hover:text-slate-200',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
            className ?? '',
        ].join(' '), children: showInlineCheck && copied
            ? _jsx(Check, { className: "h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400", "aria-hidden": "true" })
            : _jsx(Copy, { className: "h-3.5 w-3.5", "aria-hidden": "true" }) }));
}
